import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, ArrowRight } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './ui/sheet';
import { Button } from './ui/button';
import { nav, brand } from '../mock';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // On home, header overlays the dark hero when at top -> light text.
  const overlayDark = isHome && !scrolled;

  const getShellClass = () => {
    const solid = 'bg-[#faf9f5]/90 backdrop-blur-md border-b border-[#e7e5dd]';
    if (scrolled) return `${solid} shadow-[0_8px_30px_-20px_rgba(23,45,48,0.35)]`;
    if (isHome) return 'bg-transparent border-b border-transparent';
    return solid;
  };
  const shellClass = getShellClass();

  const linkColor = overlayDark ? 'text-[#e8ede9]/85 hover:text-white' : 'text-[#3a4a48] hover:text-[#1f5866]';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${shellClass}`}>
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
        <div className="flex h-[74px] items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={overlayDark ? brand.logoDark : brand.logoLight}
              alt="Herbert Project Partners"
              className="h-11 w-11 rounded-full object-cover ring-1 ring-black/5"
            />
            <div className="leading-tight">
              <div className={`font-serif text-[19px] tracking-[0.14em] ${overlayDark ? 'text-white' : 'text-[#2b3a38]'}`}>
                HERBERT
              </div>
              <div className={`text-[9px] tracking-[0.34em] font-medium ${overlayDark ? 'text-[#c6a94b]' : 'text-[#8a8d90]'}`}>
                PROJECT PARTNERS
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`nav-link text-[14px] font-medium tracking-wide ${linkColor} ${
                  location.pathname === item.to ? 'active' : ''
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Button
              onClick={() => navigate('/contact')}
              className="btn-gold rounded-full h-10 px-5 text-[13px] font-semibold shadow-none hover:shadow-[0_10px_24px_-12px_rgba(198,169,75,0.7)]"
            >
              {brand.primaryCta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <button aria-label="Open menu" className={`p-2 rounded-md ${overlayDark ? 'text-white' : 'text-[#2b3a38]'}`}>
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-[#faf9f5] border-l border-[#e7e5dd]">
                <div className="mt-4 flex items-center gap-3">
                  <img src={brand.logoLight} alt="Herbert Project Partners" className="h-10 w-10 rounded-full object-cover" />
                  <div className="font-serif text-[17px] tracking-[0.12em] text-[#2b3a38]">HERBERT</div>
                </div>
                <nav className="mt-8 flex flex-col gap-1">
                  {nav.map((item) => (
                    <SheetClose asChild key={item.to}>
                      <Link
                        to={item.to}
                        className={`py-3 px-2 rounded-md text-[15px] font-medium ${
                          location.pathname === item.to ? 'text-[#1f5866] bg-[#eef2f0]' : 'text-[#3a4a48]'
                        }`}
                      >
                        {item.label}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <SheetClose asChild>
                  <Button
                    onClick={() => navigate('/contact')}
                    className="btn-gold mt-6 w-full rounded-full h-11 text-[13px] font-semibold"
                  >
                    {brand.primaryCta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </SheetClose>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
